import { plain } from '../../lib/labels';
import { Card, Descriptions, Table, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import { Link } from 'react-router-dom';
import { adminLinks } from '../../lib/adminLinks';
import { formatHandle, validDisplayName } from '../../lib/listenerName';
import { STATUS_COLOR as RECHARGE_COLOR, STATUS_LABEL as RECHARGE_LABEL, type OrderStatus } from '../refunds/paymentLifecycle';
import {
  dateTime,
  duration,
  rupees,
  shortDateTime,
  type RecentCall,
  type RecentPayment,
  type SupportUser,
} from './api';

const dash = <span style={{ color: '#8B8B8B' }}>—</span>;

const when = (iso: string) => (
  <Tooltip title={dateTime(iso)}>
    <span style={{ whiteSpace: 'nowrap' }}>{shortDateTime(iso)}</span>
  </Tooltip>
);

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  captured: 'success',
  paid: 'success',
  success: 'success',
  failed: 'error',
  refunded: 'purple',
  created: 'default',
  pending: 'processing',
};

const paymentColumns: TableColumnsType<RecentPayment> = [
  { title: 'Paid through', dataIndex: 'gateway', render: (g: string) => <Tag>{plain(g)}</Tag> },
  {
    title: 'Status',
    dataIndex: 'status',
    // Same words and colours as the Recharges page, so a ticket and the recharge agree.
    render: (s: string) => (
      <Tag color={RECHARGE_COLOR[s as OrderStatus] ?? PAYMENT_STATUS_COLOR[s] ?? 'default'}>
        {RECHARGE_LABEL[s as OrderStatus] ?? plain(s)}
      </Tag>
    ),
  },
  { title: 'Amount', dataIndex: 'amountPaise', align: 'right', render: (p: string) => rupees(p) },
  { title: 'Time', dataIndex: 'createdAt', render: (s: string) => when(s) },
  {
    title: '',
    key: 'open',
    render: (_: unknown, p: RecentPayment) => <Link to={adminLinks.recharge(p.id)}>Open</Link>,
  },
];

const callColumns: TableColumnsType<RecentCall> = [
  { title: 'Mode', dataIndex: 'mode', render: (m: string) => <Tag color={m === 'video' ? 'purple' : 'blue'}>{plain(m)}</Tag> },
  { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{plain(s)}</Tag> },
  { title: 'Started', dataIndex: 'startedAt', render: (s: string | null) => (s ? when(s) : dash) },
  {
    title: 'Duration',
    render: (_: unknown, c) => (c.startedAt && c.endedAt ? duration(c.startedAt, c.endedAt) : dash),
  },
];

/** Who the support agent is talking to, next to the conversation. */
export function UserContextCard({ user, linkUsers = true }: { user: SupportUser; linkUsers?: boolean }) {
  // An agency never opens admin-only user / recharge pages.
  const userLink = (label: string) =>
    linkUsers ? (
      <Link to={adminLinks.user(user.id)} style={{ fontWeight: 600 }}>
        {label}
      </Link>
    ) : (
      <Typography.Text strong>{label}</Typography.Text>
    );
  return (
    <Card size="small" title="User">
      <Descriptions
        size="small"
        column={1}
        colon={false}
        styles={{ label: { width: 130, color: '#8B8B8B' } }}
        items={[
          ...(validDisplayName(user.displayName)
            ? [
                {
                  key: 'name',
                  label: 'Display name',
                  children: userLink(validDisplayName(user.displayName) ?? ''),
                },
              ]
            : []),
          {
            key: 'handle',
            label: 'Handle',
            children: validDisplayName(user.displayName) ? (
              <Typography.Text type="secondary">{formatHandle(user.handle) ?? '—'}</Typography.Text>
            ) : (
              userLink(formatHandle(user.handle) ?? '—')
            ),
          },
          {
            key: 'id',
            label: 'User id',
            children: (
              <Typography.Text copyable={{ text: user.id }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {user.id}
              </Typography.Text>
            ),
          },
          { key: 'joined', label: 'Joined', children: dateTime(user.createdAt) },
          {
            key: 'account',
            label: 'Account',
            children: user.deleted ? <Tag color="error">Deleted</Tag> : <Tag color="success">Active</Tag>,
          },
          {
            key: 'listener',
            label: 'Listener status',
            children: user.listenerStatus ? <Tag>{plain(user.listenerStatus)}</Tag> : 'Not a listener',
          },
          {
            key: 'wallet',
            label: 'Wallet balance',
            children: <strong>{`${user.walletBalanceCoins.toLocaleString('en-IN')} coins`}</strong>,
          },
        ]}
      />

      <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
        Recent payments
      </Typography.Text>
      <Table<RecentPayment>
        rowKey="id"
        size="small"
        columns={linkUsers ? paymentColumns : paymentColumns.filter((c) => c.key !== 'open')}
        dataSource={user.recentPayments}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No payments' }}
      />

      <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>
        Recent calls
      </Typography.Text>
      <Table<RecentCall>
        rowKey="id"
        size="small"
        columns={callColumns}
        dataSource={user.recentCalls}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No calls' }}
      />
    </Card>
  );
}
